import type Luminary from '@lumen5/luminary';
import type { Asset, Scene, SceneGroup } from '@lumen5/luminary';
import { VideoData } from '@lumen5/luminary';

import type { AssetType, SceneGroupType } from '../constants';
import { ASSET_TYPES, SCENE_GROUP_TYPES } from '../constants';
import type {
    AssetDescription,
    BaseAssetDescription,
    SceneCoverage,
    SceneDescription,
    SceneGroupDescription,
    TranscriptDescription,
} from '../types';

import { formatDurationMs } from './assetFormatters';
import { formatIconColors, renderColorAsString } from './assetHelpers';
import {
    getFullMediaResolution,
    getFullMediaScale,
    getFullMediaTranslation,
    getFullMediaTranslationRange,
    getMediaIdFromAsset,
    getVisibleMediaBounds,
} from './mediaHelpers';
import { getTextAndHighlightsAsMarkdown } from './textHelpers';

import { MediaRegistry } from 'js/src/agent/registries/MediaRegistry';
import { partitionTranscriptByScene } from 'js/src/agent/tools/transcript-partition';
import { AI_VOICE_SPEED_OPTIONS } from 'js/src/data/ai-voices-and-avatars/ai-voice-speeds';
import type { RootState } from 'js/src/libs/RootState';
import {
    getVoiceDrivenAssetAIVoice,
    getVoiceDrivenAssetAIVoiceSpeed,
    isAIAvatarSceneGroup,
    isAIVoiceoverSceneGroup,
    isTalkingHeadSceneGroup,
    isVoiceoverSceneGroup,
} from 'js/src/luminary/luminary-selectors';

/**
 * Which transcript words a voice-driven scene owns and its role over them, or undefined when the
 * scene has no transcript (text-on-media, etc.) or no Redux state is available. Reads the shared
 * transcript partition so a scene's reported words match what add_overlay covers by construction.
 */
function getSceneCoverage(scene: Scene, state: RootState): SceneCoverage | undefined {
    const sceneGroup = scene.sceneGroup;
    if (!sceneGroup) {
        return undefined;
    }
    const partition = partitionTranscriptByScene(state, sceneGroup);
    const entry = partition?.find((part) => part.scene.id === scene.id);
    if (!entry) {
        return undefined;
    }
    return { words: entry.words.join(' '), role: entry.role };
}

export const simplifyAssetType = (asset: Asset): AssetType => {
    // Handle stickers first - they are a special case of media assets
    if (VideoData.isMediaAsset(asset) && VideoData.isStickerAsset(asset)) {
        return ASSET_TYPES.STICKER;
    }

    // Then check classType for regular asset types
    switch (asset.classType) {
        case VideoData.TEXT_ASSET_TYPE:
            if (!asset.isEditable) {
                return ASSET_TYPES.READONLY_TEXT;
            }
            return ASSET_TYPES.TEXT;
        case VideoData.FANCY_CAPTION_ASSET_TYPE:
            return ASSET_TYPES.FANCY_CAPTION;
        case VideoData.CAPTION_ASSET_TYPE:
            return ASSET_TYPES.CAPTION;
        case VideoData.MEDIA_ASSET_TYPE:
            if (!asset.isEditable) {
                return ASSET_TYPES.READONLY_MEDIA;
            }
            return ASSET_TYPES.MEDIA;
        case VideoData.ICON_ASSET_TYPE:
            if (!asset.isEditable) {
                return ASSET_TYPES.READONLY_ICON;
            }
            return ASSET_TYPES.ICON;
        case VideoData.COLOR_ASSET_TYPE:
            return ASSET_TYPES.BACKGROUND;
        case VideoData.CHART_ASSET_TYPE:
            return ASSET_TYPES.CHART;
        case VideoData.GROUP_ASSET_TYPE:
            return ASSET_TYPES.GROUP;
        case 'WatermarkAsset':
            return ASSET_TYPES.WATERMARK;
        default:
            return asset.classType as AssetType;
    }
};

/**
 * Apply asset-specific information based on asset type.
 * Uses early returns with proper type guards for type safety.
 */
function applyAssetSpecificInfo(asset: Asset, assetInfo: BaseAssetDescription): AssetDescription {
    // Watermark assets are special media assets managed on the scene level through SceneWatermarkManager
    if (VideoData.isMediaAsset(asset) && asset.name === 'watermark_1') {
        return {
            ...assetInfo,
            type: ASSET_TYPES.WATERMARK,
        };
    }

    // Color/Background assets
    if (VideoData.isColorAsset(asset)) {
        return {
            ...assetInfo,
            type: ASSET_TYPES.BACKGROUND,
            color: renderColorAsString(asset.sourceColor),
        };
    }

    // Group assets
    if (VideoData.isAssetGroup(asset)) {
        const children = asset
            .getAssets()
            .map((child) => child.name)
            .join(', ');
        return {
            ...assetInfo,
            type: ASSET_TYPES.GROUP,
            children,
            childrenAlignment: asset.alignChildren,
        };
    }

    // Chart assets: surface the chart type and data so the agent can see what the chart
    // shows (without this they'd fall through to the bare-classType default).
    if (VideoData.isChartAsset(asset)) {
        return {
            ...assetInfo,
            type: ASSET_TYPES.CHART,
            chartType: asset.chartType || 'unknown',
            chartData: asset.chartData ?? '',
        };
    }

    // Icon assets
    if (VideoData.isIconAsset(asset)) {
        return {
            ...assetInfo,
            type: simplifyAssetType(asset),
            iconColors: formatIconColors(asset),
        };
    }

    // Fancy Caption assets (TEXT type variant)
    if (VideoData.isFancyCaptionAsset(asset)) {
        const textColors = asset.normalStyle.fill;
        const fontSize = Math.round(asset.textSize);
        const fontFamily = asset.normalStyle
            .getRendererSettings({
                leading: asset.normalStyle.leading,
                fontSize: asset.normalStyle.fontSize,
            })
            .fontFamily.split('-')[0];
        const textLines =
            asset?.renderer?.textMetricsOfActiveChunk?.normalStyle?.lines?.length ?? 0;
        const text = asset?.renderer?.textMetricsOfActiveChunk?.normalStyle?.text ?? '';

        return {
            ...assetInfo,
            type: ASSET_TYPES.FANCY_CAPTION,
            text,
            textAlignment: asset.textAlign,
            textColors: renderColorAsString(textColors),
            textLines,
            fontSize,
            fontFamily,
        };
    }

    // Text assets
    if (VideoData.isTextAsset(asset)) {
        const baseColor = asset.normalStyle.fill;
        const highlightColor = asset.highlightStyle.fill;
        const textColors =
            highlightColor !== baseColor && asset.textHighlights.length > 0
                ? `${renderColorAsString(baseColor)} and ${renderColorAsString(highlightColor)}`
                : renderColorAsString(baseColor);
        const textLines = asset?.renderer?.metrics?.lines?.length ?? 0;

        return {
            ...assetInfo,
            type: simplifyAssetType(asset),
            text: getTextAndHighlightsAsMarkdown(asset),
            textAlignment: asset.textAlign,
            textColors,
            textLines,
            fontSize: Math.round(asset.textSize),
            fontFamily: asset.normalStyle.getRendererSettings({
                leading: asset.normalStyle.leading,
                fontSize: asset.normalStyle.fontSize,
            }).fontFamily,
        };
    }

    // Sticker assets
    if (VideoData.isMediaAsset(asset) && VideoData.isStickerAsset(asset)) {
        return {
            ...assetInfo,
            type: ASSET_TYPES.STICKER,
            mediaId: getMediaIdFromAsset(asset),
        };
    }

    // Media assets (images/videos)
    if (VideoData.isMediaAsset(asset) && asset.sourceData) {
        const mediaRegistry = MediaRegistry.getInstance();
        const mediaId = getMediaIdFromAsset(asset);
        let mediaDescription = 'No description available';
        let mediaType = 'image';

        if (mediaId !== '<unknown>') {
            const representation = mediaRegistry.getRepresentation(mediaId);
            if (representation) {
                mediaDescription =
                    representation.title ||
                    representation.description ||
                    'No description available';
                mediaType = representation.type;
            }
        }

        const bounds = getVisibleMediaBounds(asset);
        const mediaResolution = getFullMediaResolution(asset);
        const mediaScale = getFullMediaScale(asset);
        const mediaTranslation = getFullMediaTranslation(asset);
        const { mediaXTranslationRange, mediaYTranslationRange } =
            getFullMediaTranslationRange(asset);

        return {
            ...assetInfo,
            type: simplifyAssetType(asset),
            mediaId,
            mediaDescription,
            mediaType,
            bounds,
            mediaResolution,
            mediaScale,
            mediaTranslation,
            mediaXTranslationRange,
            mediaYTranslationRange,
        };
    }

    // Fallback: return base asset info
    return assetInfo;
}

export const getSceneDescriptions = (
    scene: Scene,
    luminary: Luminary,
    state?: RootState,
): SceneDescription => {
    const hasSubscenes = scene.subscenes.length > 1;
    const textPositionPresets = scene.availableTextPositions;

    // Find which scene group this scene belongs to
    const sceneGroupIndex = luminary.sceneGroups.findIndex((sg) =>
        sg.scenes.some((s) => s.id === scene.id),
    );

    // Coverage needs Redux state (to tell overlay from base scenes). The storeless
    // description path (generateVideoDescriptionFromVideoData) passes no state, so it is omitted.
    const coverage = state ? getSceneCoverage(scene, state) : undefined;

    let captionAsset;
    if (scene.sceneGroup!.captions && scene.sceneGroup!.captions.asset) {
        const asset = scene.sceneGroup!.captions.asset;
        captionAsset = {
            name: asset.name,
            type: simplifyAssetType(asset),
            bounds: `${Math.round(asset.getBounds().x)} ${Math.round(asset.getBounds().y)} ${Math.round(asset.getBounds().width)} ${Math.round(asset.getBounds().height)}`,
        };
    }

    const assets = [];
    if (hasSubscenes) {
        // For scenes with subscenes, collect assets from each subscene
        const subsceneAssets: (AssetDescription & { subscene: number })[] = [];

        for (let subsceneIndex = 0; subsceneIndex < scene.subscenes.length; subsceneIndex++) {
            const subscene = scene.subscenes[subsceneIndex];
            const subsceneAssetsData = subscene
                .getAssets()
                .map((asset) => {
                    if (VideoData.isLottieAsset(asset)) {
                        return;
                    }
                    const assetInfo: BaseAssetDescription & { subscene: number } = {
                        name: asset.name,
                        type: simplifyAssetType(asset),
                        bounds: `${Math.round(asset.getBounds().x)} ${Math.round(asset.getBounds().y)} ${Math.round(asset.getBounds().width)} ${Math.round(asset.getBounds().height)}`,
                        subscene: subsceneIndex,
                    };

                    // Apply the same asset-specific logic as the non-subscene case
                    const updatedAssetInfo = applyAssetSpecificInfo(asset, assetInfo);
                    return { ...updatedAssetInfo, subscene: subsceneIndex };
                })
                .filter(Boolean) as (AssetDescription & { subscene: number })[];
            subsceneAssets.push(...subsceneAssetsData);
        }

        if (captionAsset) {
            assets.push(captionAsset);
        }

        return {
            id: scene.id,
            globalIndex: scene.globalIndex,
            sceneGroupIndex,
            design: scene.sceneDesign,
            duration: formatDurationMs(scene.duration),
            subscenes: scene.subscenes.length,
            ...(coverage ? { coverage } : {}),
            assets: subsceneAssets,
            textPositionPresets,
        };
    } else {
        // For scenes without subscenes, use the original logic
        const assets = scene
            .getAssets()
            .map((asset) => {
                if (VideoData.isLottieAsset(asset)) {
                    return;
                }
                let assetInfo: BaseAssetDescription = {
                    name: asset.name,
                    type: simplifyAssetType(asset),
                    bounds: `${Math.round(asset.getBounds().x)} ${Math.round(asset.getBounds().y)} ${Math.round(asset.getBounds().width)} ${Math.round(asset.getBounds().height)}`,
                };

                assetInfo = applyAssetSpecificInfo(asset, assetInfo);
                return assetInfo;
            })
            .filter(Boolean) as AssetDescription[];

        if (captionAsset) {
            assets.push(captionAsset);
        }

        return {
            id: scene.id,
            globalIndex: scene.globalIndex,
            sceneGroupIndex,
            design: scene.sceneDesign,
            duration: formatDurationMs(scene.duration),
            subscenes: 1,
            ...(coverage ? { coverage } : {}),
            assets,
            textPositionPresets,
        };
    }
};

// Scene Group serialization functions

export function getSceneGroupType(sceneGroup: SceneGroup): SceneGroupType {
    if (isTalkingHeadSceneGroup(sceneGroup)) return SCENE_GROUP_TYPES.RECORDED_TALKING_HEAD;
    if (isAIAvatarSceneGroup(sceneGroup)) return SCENE_GROUP_TYPES.AI_TALKING_HEAD;
    if (isVoiceoverSceneGroup(sceneGroup)) {
        return isAIVoiceoverSceneGroup(sceneGroup)
            ? SCENE_GROUP_TYPES.AI_VOICEOVER
            : SCENE_GROUP_TYPES.RECORDED_VOICEOVER;
    }
    return SCENE_GROUP_TYPES.REGULAR;
}

function getTranscriptDescription(sceneGroup: SceneGroup): TranscriptDescription | null {
    const transcriptData = sceneGroup.originalTranscript?.transcriptData;
    if (!transcriptData?.words?.length) {
        return null;
    }

    return {
        language: transcriptData.language,
        words: transcriptData.words.map((w) => ({
            word: w.word,
            startTime: w.startTime,
            endTime: w.endTime,
        })),
    };
}

/**
 * The AI voice and speed for an AI-voiceover scene group — surfaced so the agent can see (and diff)
 * what edit_ai_voiceover would change. Both come off the Luminary asset's source data (no Redux
 * needed). The stored speed is a multiplier; map it back to its named step (slowest…fastest) so the
 * read vocabulary matches what edit_ai_voiceover accepts. Empty for non-AI-voiceover groups.
 */
function getAIVoiceInfo(
    sceneGroup: SceneGroup,
): Pick<SceneGroupDescription, 'voice' | 'voiceSpeed'> {
    if (!isAIVoiceoverSceneGroup(sceneGroup)) {
        return {};
    }
    const voice = getVoiceDrivenAssetAIVoice(sceneGroup);
    const speedValue = getVoiceDrivenAssetAIVoiceSpeed(sceneGroup);
    const namedStep = AI_VOICE_SPEED_OPTIONS.find((option) => option.value === speedValue);
    const voiceSpeed = namedStep?.name ?? (speedValue != null ? `${speedValue}x (custom)` : null);
    return { voice, voiceSpeed };
}

export function getSceneGroupDescriptions(luminary: Luminary): SceneGroupDescription[] {
    return luminary.sceneGroups.map((sceneGroup, index) => {
        const type = getSceneGroupType(sceneGroup);
        const sceneIndices = sceneGroup.scenes.map((scene) =>
            luminary.scenes.findIndex((s) => s.id === scene.id),
        );

        return {
            id: sceneGroup.id,
            index,
            type,
            sceneIndices,
            duration: sceneGroup.duration,
            transcript: getTranscriptDescription(sceneGroup),
            ...getAIVoiceInfo(sceneGroup),
        };
    });
}
